<template>
  <v-container
    fluid
    class="fill-height"
  >
    <v-row
      class="ma-0"
    >
      <v-subheader class="pa-0">
        <span class="title">Informações da nota fiscal</span>
      </v-subheader>
      <v-card
        width="100%"
        :loading="isLoading"
      >
        <v-skeleton-loader
          v-if="isLoading"
          :loading="isLoading"
          type="article"
        />
        <v-container
          v-else
          fluid
          class="fill-height"
        >
          <v-row
            class="ma-0"
          >
            <v-col
              cols="12"
              sm="12"
              md="4"
              lg="4"
              xl="4"
            >
              <v-text-field
                v-model="strName"
                label="Nome completo"
                outlined
                :error-messages="strNameErrors"
              />
            </v-col>
            <v-col
              cols="12"
              sm="12"
              md="4"
              lg="4"
              xl="4"
            >
              <v-text-field
                v-model="strEmail"
                label="E-mail"
                outlined
                :error-messages="strEmailErrors"
              />
            </v-col>
            <v-col
              cols="12"
              sm="12"
              md="4"
              lg="4"
              xl="4"
            >
              <v-text-field
                v-model="strDocumentNumber"
                label="CPF/CNPJ"
                v-mask="strDocumentNumberMasks"
                outlined
                :error-messages="strDocumentNumberErrors"
              />
            </v-col>
            <v-col
              cols="12"
              sm="12"
              md="4"
              lg="4"
              xl="4"
            >
              <v-text-field
                v-model="strZipCode"
                label="CEP"
                v-mask="strZipCodeMasks"
                :loading="isLoadingAddress"
                outlined
                :error-messages="strZipCodeErrors"
              />
            </v-col>
            <v-fade-transition>
              <fragment
                v-if="showAddressFields"
              >
                <v-col
                  cols="12"
                  sm="12"
                  md="4"
                  lg="4"
                  xl="4"
                >
                  <v-text-field
                    v-model="strStreet"
                    label="Rua"
                    outlined
                    :error-messages="strStreetErrors"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="12"
                  md="2"
                  lg="2"
                  xl="2"
                >
                  <v-text-field
                    v-model="strNumber"
                    label="Número"
                    outlined
                    :error-messages="strNumberErrors"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="12"
                  md="2"
                  lg="2"
                  xl="2"
                >
                  <v-text-field
                    v-model="strComplement"
                    label="Complemento"
                    outlined
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="12"
                  md="4"
                  lg="4"
                  xl="4"
                >
                  <v-text-field
                    v-model="strDistrict"
                    label="Bairro"
                    outlined
                    :error-messages="strDistrictErrors"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="12"
                  md="4"
                  lg="4"
                  xl="4"
                >
                  <v-text-field
                    v-model="strCity"
                    label="Cidade"
                    outlined
                    :error-messages="strCityErrors"
                  />
                </v-col>
                <v-col
                  cols="12"
                  sm="12"
                  md="4"
                  lg="4"
                  xl="4"
                >
                  <v-select
                    v-model="strState"
                    label="Estado"
                    :items="statesList"
                    outlined
                    :error-messages="strStateErrors"
                  />
                </v-col>
              </fragment>
            </v-fade-transition>
          </v-row>
        </v-container>
      </v-card>
    </v-row>
    <v-row
      class="ma-0 my-2"
    >
      <v-divider/>
    </v-row>
    <v-row
      class="ma-0"
    >
      <v-btn
        :disabled="isRequesting"
        color="accent"
        outlined
        @click="onLastClick"
        :loading="PaymentMixin.isPaying"
      >
        Voltar
      </v-btn>
      <v-spacer/>
      <v-btn
        color="primary"
        @click="onNextClick"
        :loading="isRequesting || PaymentMixin.isPaying"
      >
        Próximo
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
  import {validationMixin} from "vuelidate"
  import required from "vuelidate/lib/validators/required"
  import {lt, lte, eq, isEmpty, isEqual} from "lodash/lang"
  import {forEach} from "lodash/collection"
  import {last} from "lodash/array"
  import email from "vuelidate/lib/validators/email"
  import {documentNumberValidation, unmask} from "../../helpers/utils"
  import {DOCUMENT_TYPE, PAYMENT_METHOD, PAYMENT_STEPS, STATES} from "../../helpers/payment/constants"
  import minLength from "vuelidate/lib/validators/minLength"
  import {debounce} from "lodash/function"
  import axios from "axios"
  import {NOTIFICATION_TYPES, NotificationOptions} from "../../helpers/notify"
  import {createNfeInfo, listNfeInfo, updateNfeInfo} from "../../api/api-billing"

  const VIACEP_URL = "https://viacep.com.br/ws/"

  const PaymentLastStep = Symbol.for("PaymentLastStep")
  const PaymentNextStep = Symbol.for("PaymentNextStep")
  const PaymentSetStep = Symbol.for("PaymentSetStep")
  const SetResumeNfeInfo = Symbol.for("SetResumeNfeInfo")
  const _PaymentMixin = Symbol.for("_PaymentMixin")
  const SetCdNfeInfo = Symbol.for("SetCdNfeInfo")

  export default {
    name: "NfeInfo",
    mixins: [validationMixin],
    inject: {LastStep: PaymentLastStep, NextStep: PaymentNextStep, SetResumeNfeInfo, SetStep: PaymentSetStep, _PaymentMixin, SetCdNfeInfo},
    data: () => ({
      isLoading: false,
      isRequesting: false,

      idNfeInfo: null,

      strName: null,
      strEmail: null,
      strDocumentNumber: null,

      strStreet: null,
      strNumber: null,
      strComplement: null,
      strDistrict: null,
      strState: null,
      strCity: null,
      abCountry: null,
      strZipCode: null,

      hasZipCodeError: false,
      isLoadingAddress: false,
      showAddressFields: false,
    }),
    validations() {
      return {
        strName: {
          required,
          isValid: function () {
            if (!this.strName) return false
            const strNameSplit = this.strName.split(" ")

            if (lt(strNameSplit.length, 2)) return false

            let isValid = true
            forEach(strNameSplit, str => {
              if (lt(str.length, 1)) {
                isValid = false
              }
            })
            return isValid
          }
        },
        strEmail: {
          required,
          email
        },
        strDocumentNumber: {
          required,
          isValid: function () {
            const unmaskedDocumentNumber = unmask(this.strDocumentNumber, this.strDocumentNumberMasks)
            return documentNumberValidation(unmaskedDocumentNumber)
          }
        },
        strZipCode: {
          required,
          minLength: minLength(9),
          isValid: function () {
            return !this.hasZipCodeError
          }
        },
        strStreet: {
          required
        },
        strNumber: {
          required
        },
        strDistrict: {
          required
        },
        strCity: {
          required
        },
        strState: {
          required
        }
      }
    },
    methods: {
      onNextClick() {
        this.$v.$touch()
        if (this.$v.$invalid) return

        this.isRequesting = true
        if (!this.idNfeInfo) {
          this.createNfeInfo()
            .then(() => {
              const resumeNfeInfo = {
                strName: this.strName,
                strDocumentNumber: this.strDocumentNumber,
                strZipCode: this.strZipCode
              }
              this.SetResumeNfeInfo(resumeNfeInfo)
              this.NextStep()
            })
        } else {
          this.updateNfeInfo()
            .then(() => {
              const resumeNfeInfo = {
                strName: this.strName,
                strDocumentNumber: this.strDocumentNumber,
                strZipCode: this.strZipCode
              }
              this.SetResumeNfeInfo(resumeNfeInfo)
              this.NextStep()
            })
        }
      },
      onLastClick() {
        if (isEqual(this.PaymentMixin.paymentMethod, PAYMENT_METHOD.SAVED_CREDIT_CARD)) {
          this.SetStep(PAYMENT_STEPS.PAYMENT_METHOD)
          return
        }
        this.LastStep()
      },
      async createNfeInfo() {
        const nfeInfoData = {
          str_name: this.strName,
          str_email: this.strEmail,
          str_document_number: unmask(this.strDocumentNumber, this.strDocumentNumberMasks),
          ab_document_type: this.abDocumentType,
          address_info: {
            str_street: this.strStreet,
            str_number: this.strNumber,
            str_complement: this.strComplement,
            str_district: this.strDistrict,
            str_state: this.strState,
            str_city: this.strCity,
            ab_country: this.abCountry,
            str_zip_code: unmask(this.strZipCode, this.strZipCodeMasks),
          }
        }
        return createNfeInfo(this.$apiAuth.getAuthToken(), nfeInfoData)
          .then(res => {
            const nfeInfo = res.data.data
            this.idNfeInfo = nfeInfo["id_nfe_info"]
            this.SetCdNfeInfo(nfeInfo["id_nfe_info"])
          })
          .catch(() => {
            this.$notify(new NotificationOptions({
              message: "Ocorreu algum erro ao atualizar suas informações de nota fiscal, tente novamente mais tarde.",
              type: NOTIFICATION_TYPES.ERROR
            }))
          })
          .finally(() => {
            this.isRequesting = false
          })
      },
      async updateNfeInfo() {
        const nfeInfoData = {
          str_name: this.strName,
          str_email: this.strEmail,
          str_document_number: unmask(this.strDocumentNumber, this.strDocumentNumberMasks),
          ab_document_type: this.abDocumentType,
          address_info: {
            str_street: this.strStreet,
            str_number: this.strNumber,
            str_complement: this.strComplement,
            str_district: this.strDistrict,
            str_state: this.strState,
            str_city: this.strCity,
            ab_country: this.abCountry,
            str_zip_code: unmask(this.strZipCode, this.strZipCodeMasks),
          }
        }
        return updateNfeInfo(this.$apiAuth.getAuthToken(), this.idNfeInfo, nfeInfoData)
          .then(res => {
            const nfeInfo = res.data.data
            this.idNfeInfo = nfeInfo["id_nfe_info"]
            this.SetCdNfeInfo(nfeInfo["id_nfe_info"])
          })
          .catch(() => {
            this.$notify(new NotificationOptions({
              message: "Ocorreu algum erro ao atualizar suas informações de nota fiscal, tente novamente mais tarde.",
              type: NOTIFICATION_TYPES.ERROR
            }))
          })
          .finally(() => {
            this.isRequesting = false
          })
      },
      getAddress: debounce(function () {
        if (!this.strZipCode) return
        const unmaskedStrZipCode = unmask(this.strZipCode, this.strZipCodeMasks)
        if (lt(unmaskedStrZipCode.length, 8)) return

        this.isLoadingAddress = true
        let requestUrl = `${VIACEP_URL}${unmaskedStrZipCode}/json`
        axios.get(requestUrl)
          .then(res => {
            const address = res.data
            if (address.hasOwnProperty('erro')) {
              this.$notify(new NotificationOptions({
                message: "CEP Inválido",
                type: NOTIFICATION_TYPES.ERROR
              }))
              this.hasZipCodeError = true
              return
            }

            this.hasZipCodeError = false
            this.showAddressFields = true

            this.strStreet = address["logradouro"]
            this.strDistrict = address["bairro"]
            this.strCity = address["localidade"]
            this.strState = address["uf"]
          })
          .finally(() => {
            this.isLoadingAddress = false
          })
      }, 500)
    },
    computed: {
      strNameErrors() {
        const errors = []
        if (!this.$v.strName.$dirty) return errors
        !this.$v.strName.required && errors.push("Esse campo é obrigatório")
        !this.$v.strName.isValid && errors.push("É necessário preencher com nome e sobrenome válidos")
        return errors
      },
      strEmailErrors() {
        const errors = []
        if (!this.$v.strEmail.$dirty) return errors
        !this.$v.strEmail.required && errors.push("Esse campo é obrigatório")
        !this.$v.strEmail.email && errors.push("E-mail inválido")
        return errors
      },
      strDocumentNumberMasks() {
        return ["###.###.###-##", "##.###.###/####-##"]
      },
      strDocumentNumberErrors() {
        const errors = []
        if (!this.$v.strDocumentNumber.$dirty) return errors
        !this.$v.strDocumentNumber.required && errors.push("Esse campo é obrigatório")
        !this.$v.strDocumentNumber.isValid && errors.push("CPF/CNPJ inválido")
        return errors
      },
      abDocumentType() {
        if (!this.strDocumentNumber) return DOCUMENT_TYPE.CPF

        const unmaskedDocumentNumber = unmask(this.strDocumentNumber, this.strDocumentNumberMasks)
        if (lte(unmaskedDocumentNumber.length, 11)) return DOCUMENT_TYPE.CPF
        return DOCUMENT_TYPE.CNPJ
      },
      strZipCodeMasks() {
        return ["#####-###"]
      },
      strZipCodeErrors() {
        const errors = []
        if (!this.$v.strZipCode.$dirty) return errors
        !this.$v.strZipCode.required && errors.push("Esse campo é obrigatório")
        !this.$v.strZipCode.minLength && errors.push("CEP Inválido")
        !this.$v.strZipCode.isValid && errors.push("CEP Inválido")
        return errors
      },
      strStreetErrors() {
        const errors = []
        if (!this.$v.strStreet.$dirty) return errors
        !this.$v.strStreet.required && errors.push("Esse campo é obrigatório")
        return errors
      },
      strNumberErrors() {
        const errors = []
        if (!this.$v.strNumber.$dirty) return errors
        !this.$v.strNumber.required && errors.push("Esse campo é obrigatório")
        return errors
      },
      strDistrictErrors() {
        const errors = []
        if (!this.$v.strDistrict.$dirty) return errors
        !this.$v.strDistrict.required && errors.push("Esse campo é obrigatório")
        return errors
      },
      strCityErrors() {
        const errors = []
        if (!this.$v.strCity.$dirty) return errors
        !this.$v.strCity.required && errors.push("Esse campo é obrigatório")
        return errors
      },
      statesList() {
        return STATES
      },
      strStateErrors() {
        const errors = []
        if (!this.$v.strState.$dirty) return errors
        !this.$v.strState.required && errors.push("Esse campo é obrigatório")
        return errors
      },
      PaymentMixin() {
        return this._PaymentMixin()
      },
    },
    watch: {
      strZipCode(newValue) {
        const unmaskedStrZipCode = unmask(newValue, this.strZipCodeMasks)
        if (eq(unmaskedStrZipCode.length, 8)) this.getAddress()
      }
    },
    mounted() {
      this.isLoading = true
      const listNfeInfoPromise = listNfeInfo(this.$apiAuth.getAuthToken())
        .then(res => {
          const listedNfeInfo = res.data.data
          if (!isEmpty(listedNfeInfo)) {
            const nfeInfo = last(listedNfeInfo)
            this.idNfeInfo = nfeInfo["id_nfe_info"]
            this.strName = nfeInfo["str_name"]
            this.strEmail = nfeInfo["str_email"]
            this.strDocumentNumber = nfeInfo["str_document_number"]

            const addressInfo = nfeInfo["address_info"]
            this.strStreet = addressInfo["str_street"]
            this.strNumber = addressInfo["str_number"]
            this.strComplement = addressInfo["str_complement"]
            this.strDistrict = addressInfo["str_district"]
            this.strState = addressInfo["str_state"]
            this.strCity = addressInfo["str_city"]
            this.strZipCode = addressInfo["str_zip_code"]
          }
        })

      const whoamiPromise = this.$apiAuth.whoami()
        .then(res => {
          const userData = res.data
          this.abCountry = userData["ab_country"]
        })

      Promise.all([listNfeInfoPromise, whoamiPromise])
        .finally(() => {
          this.isLoading = false
        })
    }
  }
</script>

<style scoped>

</style>
